import {
  Box,
  Button,
  Link,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import {
  ConfidenceWebsiteLinkEncore,
  LoadingOverlay,
} from '@spotify/confidence-core-react';

import { TransitionScreen } from '../TransitionScreen';
import {
  CreateAccountFormProvider,
  useCreateAccountFormContext,
} from './CreateAccountFormProvider';
import { ExperimentationLoader } from './ExperimentationLoader';
import { WorkspaceSetupForm } from './WorkspaceSetupForm';
import type { CreatedAccount } from './hooks';

import styles from './CreateAccountFormV2.module.css';

const contactSupportLink = (
  <Link
    href="mailto:confidence-support@spotify.com"
    target="_blank"
    rel="noopener noreferrer"
  >
    Contact support <VisuallyHidden>(opens in new window)</VisuallyHidden>
  </Link>
);

const verificationEmailHelperText = (
  <>Didn&apos;t receive a verification e-mail? {contactSupportLink}.</>
);

const CreateAccountFormV2Content = () => {
  const {
    screen,
    showOverlay,
    error,
    handleResultDismissed,
    recheckEmailVerification,
    handleLogin,
  } = useCreateAccountFormContext();
  switch (screen) {
    case 'loading':
      return <LoadingOverlay loading />;

    case 'verify_email':
      return (
        <TransitionScreen
          title="Verify your e-mail address"
          description="We've sent you a verification email. Please click the link in the email to verify your address."
          actions={
            <Button
              variant="primary"
              size="medium"
              onClick={recheckEmailVerification}
            >
              I&apos;ve verified my email
            </Button>
          }
          helperText={verificationEmailHelperText}
        />
      );

    case 'under_review':
      return (
        <TransitionScreen
          title="Thank you for signing up for Confidence"
          description="We are currently reviewing your account request. You will receive an email once it has been approved."
          helperText={<>Have questions? {contactSupportLink}.</>}
        />
      );

    case 'workspace_setup':
      return (
        <>
          {showOverlay && (
            <div className={styles.overlay}>
              <ExperimentationLoader
                error={error}
                onResultDismissed={handleResultDismissed}
              />
            </div>
          )}
          <WorkspaceSetupForm />
        </>
      );

    case 'login_verify_email':
      return (
        <div data-track-id="region.verify-email" data-track-impression>
          <TransitionScreen
            title="Verify your e&#8209;mail address"
            description="Please click on the link we sent you to verify your e&#8209;mail address."
            actions={
              <Button variant="primary" size="medium" onClick={handleLogin}>
                Log in
              </Button>
            }
            helperText={verificationEmailHelperText}
          />
        </div>
      );

    case 'welcome':
      return (
        <div data-track-id="region.welcome" data-track-impression>
          <TransitionScreen
            title="Welcome to Confidence"
            description="Use your work e-mail address, not a personal account."
            actions={
              <Button
                data-testid="signup-continue"
                variant="primary"
                size="medium"
                onClick={handleLogin}
              >
                Continue
              </Button>
            }
            helperText={
              <>
                <strong>Already using Confidence in your company?</strong>
                <br />
                Ask a teammate for an invite or go to the{' '}
                <Link href="/login">login page</Link> if single sign-on is
                enabled.
                <Box paddingBlockStart="tighter">
                  For more information about how Spotify processes your data,
                  please read our{' '}
                  <ConfidenceWebsiteLinkEncore route="/privacy-policy">
                    Privacy Policy
                  </ConfidenceWebsiteLinkEncore>
                  .
                </Box>
              </>
            }
          />
        </div>
      );

    default:
      return null;
  }
};

export const CreateAccountFormV2 = ({
  inviteId,
  onCreate,
  token,
}: {
  token?: string;
  inviteId?: string;
  onCreate: (account?: CreatedAccount) => void;
}) => (
  <CreateAccountFormProvider
    inviteId={inviteId}
    token={token}
    onCreate={onCreate}
  >
    <CreateAccountFormV2Content />
  </CreateAccountFormProvider>
);
